/**
 * Ambient music (asked for on 2026-09-28: "make a general ground level,
 * above ground level, and cave music").
 *
 * Like the sound effects in sound.ts, nothing is played from a file: each
 * piece is a handful of rules — a key, a pace, a turn of chords, a lead
 * voice and a wandering tune — that write out one bar at a time, a little
 * ahead of when it is heard, so it never loops and there is nothing to
 * download.
 *
 * It started as three pieces, one for each kind of level:
 *
 * - The ground is pastoral: a major pentatonic tune on a soft reed over
 *   slow chords, and now and then a bird. Since 2026-10-27 it is the
 *   Shire's.
 * - Above the ground is airy: the bright fourth of the Lydian mode, bells
 *   that ring on, a high thin pad and wind that rises and falls. It plays
 *   over every region but Mordor.
 * - The caves are low and close: a drone on a Phrygian root and its fifth,
 *   a few dark notes a long way apart, and water dripping.
 *
 * Then the east and Mordor had their own (2026-10-12: "Add new overland
 * music when east of the misty mountains", "Create a music for Mordor"):
 *
 * - The east is wide and older: a horn in the Dorian mode, its bright
 *   sixth over a minor root, slow open chords, and now and then a hawk
 *   crying high overhead. Since 2026-10-27 it is Wilderland's.
 * - Mordor is slow and heavy: low growling brass in the Locrian mode, whose
 *   fifth is the flattened one, over tritones, and war drums beating
 *   "doom, doom" at the head of every bar. It plays on Mordor's heights too.
 *
 * And since 2026-10-27 ("make different music for different regions",
 * then "do it") every region has one:
 *
 * - Eriador's wilds, the Rangers' country: a lonelier Shire, a low flute
 *   in D minor pentatonic, slower and emptier, with the wind.
 * - The old woods (the Old Forest, the Barrow-downs, Fangorn): slow
 *   wooden notes wandering a whole-tone scale, and a bough creaking.
 * - The cold North: thin strings high in the Aeolian mode over a drone,
 *   very slow, and the wind.
 * - The Elves' lands: a harp in E Lydian over a bright soft pad, and a
 *   glint of chimes.
 * - Mirkwood: a dark reed in A harmonic minor, long silences, and leaves
 *   rustling.
 * - Rohan: a fiddle in D Mixolydian, quick, over a galloping drum.
 * - Gondor: stately brass in C major, slow processional chords, and a
 *   distant bell.
 * - The dead lands before the Black Gate: a wavering voice in a whole-tone
 *   scale over close clusters, and will-o'-the-wisps chiming.
 * - The Easterlings' and the Haradrim's lands: a nasal reed in the
 *   Phrygian dominant mode, the desert's, over a hand drum's pattern.
 * - What is left of Númenor: a slow choir in C minor, and the sea.
 * - The dwarves' halls: a low choir in D Dorian over a drone, and the
 *   ring of an anvil.
 * - The tombs and the haunts of the dead: a drone a semitone apart, a few
 *   dark notes, and whispering.
 * - The Elvenking's hall: the Elves' harp, lower, darker, and dripping.
 *
 * Which region you are in is the server's to say (You.music, from
 * landMusic); the level decides the rest (trackFor).
 *
 * Moving between them fades one out as the other comes in, over a few
 * seconds, so a stair is heard as a change of place rather than a cut.
 */

/** Which piece is playing. */
export type Track =
  | "ground"
  | "east"
  | "mordor"
  | "above"
  | "cave"
  | "eriador"
  | "oldwood"
  | "north"
  | "elven"
  | "mirkwood"
  | "rohan"
  | "gondor"
  | "deadlands"
  | "south"
  | "numenor"
  | "dwarf"
  | "tomb"
  | "elvenhalls";

/** The pieces that are for under the ground. */
const UNDERGROUND = new Set<string>(["dwarf", "tomb", "elvenhalls"]);

/**
 * The piece for a level and the region (the server's You.music): under
 * the ground, a dwarf hall's, a tomb's or the Elvenking's, and the caves
 * anywhere else; over the ground, Mordor's in Mordor and the heights'
 * bells everywhere else; on the ground, the region's own, and the Shire's
 * country tune where it names none.
 */
export function trackFor(z: number | undefined, land = ""): Track | null {
  if (z === undefined) return null;
  if (z < 0) return UNDERGROUND.has(land) ? (land as Track) : "cave";
  if (z > 0) return land === "mordor" ? "mordor" : "above";
  return land !== "" && Object.prototype.hasOwnProperty.call(PIECES, land) && land !== "above" && land !== "cave" ? (land as Track) : "ground";
}

type Colour = "bird" | "wind" | "drip" | "hawk" | "drum" | "creak" | "rustle" | "chime" | "bell" | "waves" | "anvil" | "whisper" | "gallop" | "handdrum";

type Lead = "reed" | "horn" | "brass" | "bell" | "dark" | "flute" | "wood" | "strings" | "harp" | "clarinet" | "fiddle" | "trumpet" | "wisp" | "shawm" | "choir";

/** One note of a bar: when it starts and how long it lasts, in beats. */
export interface Note {
  at: number;
  beats: number;
  midi: number;
  voice: "tune" | "pad" | "drone" | Colour;
}

interface Piece {
  bpm: number;
  /** The key note, as a MIDI number. */
  root: number;
  /** The scale, in semitones up from the root, inside one octave. */
  scale: number[];
  /** Each bar's chord, in semitones from the root an octave down; cycled. */
  chords: number[][];
  /** The chords' wave, how far they are let through, and whether a
   *  second voice a shade sharp thickens them. */
  pad: { wave: OscillatorType; cut: number; detune: boolean };
  /** A drone on the root and its fifth under every bar. */
  drone?: boolean;
  /** What plays the tune. */
  lead: Lead;
  /** The tune's range, in steps of the scale up from the root. */
  low: number;
  high: number;
  /** The chance a beat of the tune is left empty. */
  rest: number;
  /** How long a tune's note may be, in beats. */
  lengths: number[];
  /** The colour laid over it, and how many to a bar at most. */
  colour: Colour;
  colourMost: number;
  /** If set, a bar has one colour at this chance, or none, rather than
   *  any number up to colourMost with even odds: for a colour that
   *  should be rare. */
  colourOdds?: number;
}

const soft = { wave: "sine", cut: 900, detune: true } as const;

export const PIECES: Record<Track, Piece> = {
  ground: {
    bpm: 76,
    root: 55, // G
    scale: [0, 2, 4, 7, 9],
    chords: [[0, 4, 7], [5, 9, 12], [-3, 4, 9], [2, 7, 11]], // G C Em D
    pad: soft,
    lead: "reed",
    low: 0,
    high: 9,
    rest: 0.35,
    lengths: [1, 1, 2],
    colour: "bird",
    colourMost: 1,
  },
  east: {
    bpm: 66,
    root: 57, // A
    scale: [0, 2, 3, 5, 7, 9, 10],
    chords: [[0, 3, 7], [5, 9, 12], [-2, 2, 5], [0, 7, 10]], // Am D G Am7
    pad: soft,
    lead: "horn",
    low: 0,
    high: 9,
    rest: 0.4,
    lengths: [1, 2, 2, 3],
    colour: "hawk",
    colourMost: 1,
    colourOdds: 0.2,
  },
  mordor: {
    bpm: 50,
    root: 40, // E
    scale: [0, 1, 3, 5, 6, 8, 10],
    chords: [[12, 18], [13, 19], [12, 18], [11, 17]], // E-B♭ F-B E-B♭ E♭-A
    pad: { wave: "sawtooth", cut: 300, detune: true },
    lead: "brass",
    low: 7,
    high: 13,
    rest: 0.55,
    lengths: [2, 3, 4],
    colour: "drum",
    colourMost: 1,
  },
  above: {
    bpm: 60,
    root: 65, // F
    scale: [0, 2, 4, 6, 7, 9, 11],
    chords: [[0, 4, 7, 11], [2, 6, 9, 14], [4, 7, 11, 14], [0, 7, 9, 14]], // Fmaj7 G/F Am Fsus
    pad: { wave: "sine", cut: 2400, detune: true },
    lead: "bell",
    low: 0,
    high: 11,
    rest: 0.5,
    lengths: [2, 3],
    colour: "wind",
    colourMost: 1,
  },
  cave: {
    bpm: 48,
    root: 38, // D
    scale: [0, 1, 3, 5, 7, 8, 10],
    chords: [[0, 7], [1, 8], [0, 7], [-2, 5]],
    pad: { wave: "triangle", cut: 900, detune: false },
    drone: true,
    lead: "dark",
    low: 7,
    high: 14,
    rest: 0.7,
    lengths: [2, 4],
    colour: "drip",
    colourMost: 2,
  },
  eriador: {
    bpm: 64,
    root: 50, // D
    scale: [0, 3, 5, 7, 10],
    chords: [[0, 3, 7], [-4, 0, 3], [3, 7, 10], [-2, 2, 5]], // Dm B♭ F C
    pad: soft,
    lead: "flute",
    low: 3,
    high: 10,
    rest: 0.45,
    lengths: [1, 2, 2, 3],
    colour: "wind",
    colourMost: 1,
    colourOdds: 0.3,
  },
  oldwood: {
    bpm: 54,
    root: 45, // A
    scale: [0, 2, 4, 6, 8, 10],
    chords: [[0, 4, 8], [2, 6, 10], [0, 4, 8], [-2, 4, 8]], // augmented, a tone apart
    pad: { wave: "triangle", cut: 600, detune: true },
    lead: "wood",
    low: 6,
    high: 14,
    rest: 0.5,
    lengths: [1, 1, 2],
    colour: "creak",
    colourMost: 1,
    colourOdds: 0.3,
  },
  north: {
    bpm: 44,
    root: 52, // E
    scale: [0, 2, 3, 5, 7, 8, 10],
    chords: [[0, 3, 7], [-4, 0, 3], [5, 8, 12], [0, 7, 12]], // Em C Am Em5
    pad: { wave: "sine", cut: 1200, detune: true },
    drone: true,
    lead: "strings",
    low: 7,
    high: 14,
    rest: 0.5,
    lengths: [2, 3, 4],
    colour: "wind",
    colourMost: 1,
  },
  elven: {
    bpm: 58,
    root: 64, // E
    scale: [0, 2, 4, 6, 7, 9, 11],
    chords: [[0, 4, 7], [2, 6, 9], [-3, 0, 4], [5, 9, 12]], // E F♯ C♯m A
    pad: { wave: "sine", cut: 2400, detune: true },
    lead: "harp",
    low: 0,
    high: 10,
    rest: 0.25,
    lengths: [1, 1, 1, 2],
    colour: "chime",
    colourMost: 1,
    colourOdds: 0.35,
  },
  mirkwood: {
    bpm: 52,
    root: 57, // A
    scale: [0, 2, 3, 5, 7, 8, 11],
    chords: [[0, 3, 7], [5, 8, 12], [-5, -1, 2], [0, 3, 7]], // Am Dm E Am
    pad: { wave: "triangle", cut: 600, detune: true },
    lead: "clarinet",
    low: 0,
    high: 8,
    rest: 0.6,
    lengths: [2, 3, 4],
    colour: "rustle",
    colourMost: 2,
  },
  rohan: {
    bpm: 96,
    root: 50, // D
    scale: [0, 2, 4, 5, 7, 9, 10],
    chords: [[0, 4, 7], [-2, 2, 5], [5, 9, 12], [0, 7, 12]], // D C G D5
    pad: soft,
    lead: "fiddle",
    low: 4,
    high: 13,
    rest: 0.3,
    lengths: [1, 1, 2],
    colour: "gallop",
    colourMost: 1,
  },
  gondor: {
    bpm: 63,
    root: 48, // C
    scale: [0, 2, 4, 5, 7, 9, 11],
    chords: [[0, 4, 7], [5, 9, 12], [-3, 0, 4], [-5, -1, 2]], // C F Am G
    pad: { wave: "sine", cut: 1400, detune: true },
    lead: "trumpet",
    low: 7,
    high: 14,
    rest: 0.4,
    lengths: [1, 2, 2, 3],
    colour: "bell",
    colourMost: 1,
    colourOdds: 0.25,
  },
  deadlands: {
    bpm: 40,
    root: 50, // D
    scale: [0, 2, 4, 6, 8, 10],
    chords: [[0, 1, 6], [2, 3, 8], [0, 1, 6], [-2, -1, 4]], // clusters
    pad: { wave: "sine", cut: 700, detune: true },
    lead: "wisp",
    low: 6,
    high: 13,
    rest: 0.6,
    lengths: [2, 3],
    colour: "chime",
    colourMost: 2,
  },
  south: {
    bpm: 84,
    root: 52, // E
    scale: [0, 1, 4, 5, 7, 8, 10],
    chords: [[0, 4, 7], [1, 5, 8], [0, 4, 7], [-2, 1, 5]], // E F E Dm
    pad: { wave: "triangle", cut: 900, detune: true },
    lead: "shawm",
    low: 3,
    high: 11,
    rest: 0.3,
    lengths: [1, 1, 2],
    colour: "handdrum",
    colourMost: 1,
  },
  numenor: {
    bpm: 46,
    root: 48, // C
    scale: [0, 2, 3, 5, 7, 8, 10],
    chords: [[0, 3, 7], [-4, 0, 3], [3, 7, 10], [-2, 2, 5]], // Cm A♭ E♭ B♭
    pad: soft,
    lead: "choir",
    low: 5,
    high: 12,
    rest: 0.5,
    lengths: [2, 3, 4],
    colour: "waves",
    colourMost: 1,
  },
  dwarf: {
    bpm: 56,
    root: 38, // D
    scale: [0, 2, 3, 5, 7, 9, 10],
    chords: [[0, 3, 7], [5, 9, 12], [0, 3, 7], [-2, 2, 5]], // Dm G Dm C
    pad: { wave: "triangle", cut: 700, detune: true },
    drone: true,
    lead: "choir",
    low: 7,
    high: 13,
    rest: 0.45,
    lengths: [2, 2, 3],
    colour: "anvil",
    colourMost: 1,
    colourOdds: 0.4,
  },
  tomb: {
    bpm: 40,
    root: 37, // C♯
    scale: [0, 1, 3, 5, 7, 8, 10],
    chords: [[0, 1], [0, 1], [-1, 0], [0, 1]],
    pad: { wave: "triangle", cut: 500, detune: false },
    drone: true,
    lead: "dark",
    low: 7,
    high: 13,
    rest: 0.75,
    lengths: [2, 4],
    colour: "whisper",
    colourMost: 1,
    colourOdds: 0.4,
  },
  elvenhalls: {
    bpm: 52,
    root: 52, // E
    scale: [0, 2, 4, 6, 7, 9, 11],
    chords: [[0, 4, 7], [2, 6, 9], [-3, 0, 4], [5, 9, 12]],
    pad: { wave: "triangle", cut: 900, detune: true },
    lead: "harp",
    low: 0,
    high: 7,
    rest: 0.4,
    lengths: [1, 2, 2],
    colour: "drip",
    colourMost: 1,
  },
};

const BEATS = 4;

/** Where the tune has got to, carried from one bar to the next. */
export interface Tune {
  step: number;
}

function pitch(p: Piece, step: number): number {
  const n = p.scale.length;
  const oct = Math.floor(step / n);
  return p.root + 12 * oct + p.scale[((step % n) + n) % n];
}

/**
 * One bar of a piece: the chord under it, the drone if it has one, a tune
 * that wanders a step or two at a time within its range, and the colour.
 * `rand` is where the dice come from, so a test can fix them.
 */
export function barNotes(track: Track, bar: number, tune: Tune, rand: () => number = Math.random): Note[] {
  const p = PIECES[track];
  const out: Note[] = [];
  const chord = p.chords[bar % p.chords.length];
  for (const c of chord) out.push({ at: 0, beats: BEATS, midi: p.root - 12 + c, voice: "pad" });
  if (p.drone) {
    out.push({ at: 0, beats: BEATS, midi: p.root - 12, voice: "drone" });
    out.push({ at: 0, beats: BEATS, midi: p.root - 5, voice: "drone" });
  }
  let beat = 0;
  while (beat < BEATS) {
    const len = Math.min(BEATS - beat, p.lengths[Math.floor(rand() * p.lengths.length)]);
    if (rand() >= p.rest) {
      tune.step += Math.floor(rand() * 5) - 2;
      if (tune.step < p.low) tune.step = p.low + (p.low - tune.step);
      if (tune.step > p.high) tune.step = p.high - (tune.step - p.high);
      out.push({ at: beat, beats: len, midi: pitch(p, tune.step), voice: "tune" });
    }
    beat += len;
  }
  // The drums keep time, so they are written out rather than thrown.
  if (p.colour === "drum") {
    // Doom, doom at the head of every bar, and every other bar or so a
    // third beat answering late.
    out.push({ at: 0, beats: 0.5, midi: 0, voice: "drum" });
    out.push({ at: 1, beats: 0.5, midi: 0, voice: "drum" });
    if (rand() < 0.5) out.push({ at: 3, beats: 0.5, midi: 0, voice: "drum" });
    return out;
  }
  if (p.colour === "gallop") {
    // Hooves: ba-da-dum on the first and third beats.
    for (const b of [0, 2]) for (const k of [0, 1 / 3, 2 / 3]) out.push({ at: b + k, beats: 0.25, midi: k === 2 / 3 ? 0 : 1, voice: "gallop" });
    return out;
  }
  if (p.colour === "handdrum") {
    // Doum on the one, tek tek after, and a tek more now and then.
    out.push({ at: 0, beats: 0.5, midi: 0, voice: "handdrum" });
    out.push({ at: 1.5, beats: 0.25, midi: 1, voice: "handdrum" });
    out.push({ at: 2, beats: 0.5, midi: 0, voice: "handdrum" });
    out.push({ at: 3, beats: 0.25, midi: 1, voice: "handdrum" });
    if (rand() < 0.5) out.push({ at: 3.5, beats: 0.25, midi: 1, voice: "handdrum" });
    return out;
  }
  const colours = p.colourOdds !== undefined ? (rand() < p.colourOdds ? 1 : 0) : Math.floor(rand() * (p.colourMost + 1));
  for (let i = 0; i < colours; i++) {
    switch (p.colour) {
      case "wind":
      case "waves":
        out.push({ at: 0, beats: BEATS, midi: 0, voice: p.colour });
        break;
      case "hawk":
        out.push({ at: rand() * (BEATS - 1.5), beats: 1.5, midi: 90 + Math.floor(rand() * 5), voice: "hawk" });
        break;
      case "bird":
        out.push({ at: rand() * (BEATS - 0.25), beats: 0.25, midi: 91 + Math.floor(rand() * 6), voice: "bird" });
        break;
      case "drip":
        out.push({ at: rand() * (BEATS - 0.1), beats: 0.1, midi: 84 + Math.floor(rand() * 8), voice: "drip" });
        break;
      case "creak":
        out.push({ at: rand() * (BEATS - 1), beats: 1, midi: 30 + Math.floor(rand() * 6), voice: "creak" });
        break;
      case "rustle":
        out.push({ at: rand() * (BEATS - 0.5), beats: 0.5, midi: 0, voice: "rustle" });
        break;
      case "chime":
        out.push({ at: rand() * (BEATS - 1), beats: 1, midi: 96 + Math.floor(rand() * 7), voice: "chime" });
        break;
      case "bell":
        out.push({ at: rand() * (BEATS - 1), beats: 1, midi: rand() < 0.5 ? 67 : 72, voice: "bell" });
        break;
      case "anvil":
        out.push({ at: Math.floor(rand() * 3), beats: 1, midi: 84 + Math.floor(rand() * 4), voice: "anvil" });
        break;
      case "whisper":
        out.push({ at: rand() * (BEATS - 1.5), beats: 1.5, midi: 0, voice: "whisper" });
        break;
    }
  }
  return out;
}

type Ctx = BaseAudioContext;

const hz = (midi: number) => 440 * 2 ** ((midi - 69) / 12);

/** A voice that swells in over `a` seconds, holds, and dies away over `r`. */
function shaped(ctx: Ctx, out: AudioNode, t: number, peak: number, a: number, hold: number, r: number): GainNode {
  const g = ctx.createGain();
  g.gain.setValueAtTime(0.0001, t);
  g.gain.exponentialRampToValueAtTime(peak, t + a);
  g.gain.setValueAtTime(peak, t + a + hold);
  g.gain.exponentialRampToValueAtTime(0.0001, t + a + hold + r);
  g.connect(out);
  return g;
}

function tone(ctx: Ctx, type: OscillatorType, f: number, to: AudioNode, t: number, dur: number): OscillatorNode {
  const o = ctx.createOscillator();
  o.type = type;
  o.frequency.setValueAtTime(f, t);
  o.connect(to);
  o.start(t);
  o.stop(t + dur + 0.05);
  return o;
}

function lowpass(ctx: Ctx, f: number, out: AudioNode): BiquadFilterNode {
  const b = ctx.createBiquadFilter();
  b.type = "lowpass";
  b.frequency.value = f;
  b.connect(out);
  return b;
}

function bandpass(ctx: Ctx, f: number, q: number, out: AudioNode): BiquadFilterNode {
  const b = ctx.createBiquadFilter();
  b.type = "bandpass";
  b.frequency.value = f;
  b.Q.value = q;
  b.connect(out);
  return b;
}

/** Wobbles o's pitch `rate` times a second by `depth` of it, from t to end. */
function vibrato(ctx: Ctx, o: OscillatorNode, f: number, rate: number, depth: number, t: number, end: number) {
  const lfo = ctx.createOscillator();
  lfo.frequency.value = rate;
  const d = ctx.createGain();
  d.gain.value = f * depth;
  lfo.connect(d);
  d.connect(o.frequency);
  lfo.start(t);
  lfo.stop(end);
}

/** A burst of the noise buffer, from t for dur seconds, into `to`. */
function hiss(ctx: Ctx, noise: AudioBuffer, to: AudioNode, t: number, dur: number) {
  const src = ctx.createBufferSource();
  src.buffer = noise;
  src.loop = true;
  src.connect(to);
  src.start(t);
  src.stop(t + dur + 0.05);
}

/** One note of the tune, on the piece's lead. */
function lead(ctx: Ctx, out: AudioNode, p: Piece, midi: number, at: number, dur: number, beat: number, noise: AudioBuffer) {
  const f = hz(midi);
  switch (p.lead) {
    case "brass": {
      // Low brass, two reeds a shade apart growling through a dark
      // filter, slow to speak and slow to die.
      const g = shaped(ctx, lowpass(ctx, 500, out), at, 0.06, 0.3, dur * 0.6, dur * 0.5);
      tone(ctx, "sawtooth", f, g, at, dur * 1.1 + 0.3);
      tone(ctx, "sawtooth", f * 1.006, g, at, dur * 1.1 + 0.3);
      break;
    }
    case "horn": {
      // A horn: a buzzing reed through a dark filter, swelling in, with
      // its octave below to give it body.
      const g = shaped(ctx, lowpass(ctx, 1100, out), at, 0.05, 0.12, dur * 0.7, dur * 0.4);
      tone(ctx, "sawtooth", f, g, at, dur * 1.1 + 0.12);
      tone(ctx, "sine", hz(midi - 12), shaped(ctx, out, at, 0.03, 0.12, dur * 0.7, dur * 0.4), at, dur * 1.1 + 0.12);
      break;
    }
    case "bell": {
      // A bell: struck, and left to ring.
      const g = shaped(ctx, out, at, 0.07, 0.01, 0, dur + beat * 2);
      tone(ctx, "sine", f, g, at, dur + beat * 2);
      tone(ctx, "sine", f * 2.76, shaped(ctx, out, at, 0.012, 0.01, 0, beat), at, beat);
      break;
    }
    case "dark": {
      const g = shaped(ctx, lowpass(ctx, 700, out), at, 0.06, 0.15, dur * 0.5, dur);
      tone(ctx, "triangle", f, g, at, dur * 1.5 + 0.15);
      break;
    }
    case "reed": {
      // A soft reed: a triangle with a slow vibrato, rounded off.
      const g = shaped(ctx, lowpass(ctx, 1800, out), at, 0.06, 0.06, dur * 0.6, dur * 0.5);
      const o = tone(ctx, "triangle", f, g, at, dur * 1.1 + 0.06);
      vibrato(ctx, o, f, 5, 0.004, at, at + dur * 1.1 + 0.1);
      break;
    }
    case "flute": {
      // A low flute: breathier and slower to speak than the reed.
      const g = shaped(ctx, lowpass(ctx, 2200, out), at, 0.06, 0.1, dur * 0.6, dur * 0.5);
      const o = tone(ctx, "sine", f, g, at, dur * 1.1 + 0.1);
      vibrato(ctx, o, f, 4.5, 0.005, at, at + dur * 1.1 + 0.15);
      tone(ctx, "triangle", f, shaped(ctx, lowpass(ctx, 1500, out), at, 0.012, 0.1, dur * 0.6, dur * 0.5), at, dur * 1.1 + 0.1);
      hiss(ctx, noise, bandpass(ctx, f * 2, 2, shaped(ctx, out, at, 0.006, 0.05, 0.05, 0.1)), at, 0.25);
      break;
    }
    case "wood": {
      // Struck wood, a marimba's: quick to sound and quick to go, with a
      // knock on top.
      tone(ctx, "triangle", f, shaped(ctx, lowpass(ctx, 1500, out), at, 0.09, 0.005, 0, beat * 1.2), at, beat * 1.2);
      tone(ctx, "sine", f * 4, shaped(ctx, out, at, 0.012, 0.003, 0, beat * 0.15), at, beat * 0.2);
      break;
    }
    case "strings": {
      // Thin strings, two a shade apart, bowed in slowly.
      const g = shaped(ctx, lowpass(ctx, 1600, out), at, 0.03, Math.min(0.6, dur * 0.4), dur * 0.5, dur * 0.6);
      tone(ctx, "sawtooth", f, g, at, dur * 1.5 + 0.6);
      tone(ctx, "sawtooth", f * 1.004, g, at, dur * 1.5 + 0.6);
      break;
    }
    case "harp": {
      // Plucked, and let ring a long while.
      tone(ctx, "triangle", f, shaped(ctx, lowpass(ctx, 3000, out), at, 0.07, 0.004, 0, beat * 2.5), at, beat * 2.5);
      tone(ctx, "sine", f * 2, shaped(ctx, out, at, 0.015, 0.004, 0, beat * 0.6), at, beat * 0.6);
      break;
    }
    case "clarinet": {
      // A hollow, dark reed: a square wave's odd harmonics, filtered low.
      const g = shaped(ctx, lowpass(ctx, 900, out), at, 0.035, 0.08, dur * 0.6, dur * 0.5);
      tone(ctx, "square", f, g, at, dur * 1.1 + 0.08);
      break;
    }
    case "fiddle": {
      // A fiddle: bright, quick to bite, with a singer's vibrato.
      const g = shaped(ctx, lowpass(ctx, 2600, out), at, 0.035, 0.04, dur * 0.6, dur * 0.3);
      const o = tone(ctx, "sawtooth", f, g, at, dur * 0.9 + 0.04);
      vibrato(ctx, o, f, 6, 0.005, at, at + dur * 0.9 + 0.1);
      break;
    }
    case "trumpet": {
      // Brass, brighter than Mordor's and quicker to speak.
      const g = shaped(ctx, lowpass(ctx, 1600, out), at, 0.045, 0.05, dur * 0.7, dur * 0.35);
      tone(ctx, "sawtooth", f, g, at, dur * 1.05 + 0.05);
      tone(ctx, "sawtooth", f * 1.003, g, at, dur * 1.05 + 0.05);
      break;
    }
    case "wisp": {
      // A thin voice that will not keep still.
      const g = shaped(ctx, out, at, 0.05, dur * 0.4, dur * 0.2, dur * 0.6);
      const o = tone(ctx, "sine", f, g, at, dur * 1.2);
      vibrato(ctx, o, f, 3, 0.015, at, at + dur * 1.2 + 0.05);
      break;
    }
    case "shawm": {
      // A nasal double reed: a square and a saw through a narrow band.
      const g = shaped(ctx, bandpass(ctx, 1200, 1.2, out), at, 0.07, 0.03, dur * 0.6, dur * 0.3);
      const a = tone(ctx, "square", f, g, at, dur * 0.9 + 0.03);
      const b = tone(ctx, "sawtooth", f * 1.002, g, at, dur * 0.9 + 0.03);
      vibrato(ctx, a, f, 5.5, 0.006, at, at + dur * 0.9 + 0.1);
      vibrato(ctx, b, f, 5.5, 0.006, at, at + dur * 0.9 + 0.1);
      break;
    }
    case "choir": {
      // Voices: soft and slow, a few a shade apart, singing "aah".
      const g = shaped(ctx, lowpass(ctx, 1400, out), at, 0.045, Math.min(0.6, dur * 0.4), dur * 0.4, dur * 0.6);
      tone(ctx, "sine", f, g, at, dur * 1.4 + 0.6);
      tone(ctx, "triangle", f * 1.005, g, at, dur * 1.4 + 0.6);
      tone(ctx, "triangle", f * 0.995, g, at, dur * 1.4 + 0.6);
      break;
    }
  }
}

/** Turns one bar's notes into sound, the bar starting at `t`. */
export function renderBar(ctx: Ctx, out: AudioNode, track: Track, notes: Note[], t: number, noise: AudioBuffer) {
  const p = PIECES[track];
  const beat = 60 / p.bpm;
  for (const n of notes) {
    const at = t + n.at * beat;
    const dur = n.beats * beat;
    switch (n.voice) {
      case "pad": {
        // Swells in over most of a beat and overlaps the next bar's chord.
        const g = shaped(ctx, lowpass(ctx, p.pad.cut, out), at, 0.035, beat, dur - beat, beat * 1.5);
        tone(ctx, p.pad.wave, hz(n.midi), g, at, dur + beat * 1.5);
        if (p.pad.detune) tone(ctx, "sine", hz(n.midi) * 1.003, g, at, dur + beat * 1.5);
        break;
      }
      case "drone": {
        const g = shaped(ctx, lowpass(ctx, 220, out), at, 0.05, beat * 1.5, dur - beat * 1.5, beat * 2);
        tone(ctx, "sawtooth", hz(n.midi), g, at, dur + beat * 2);
        break;
      }
      case "tune":
        lead(ctx, out, p, n.midi, at, dur, beat, noise);
        break;
      case "bird": {
        // Two quick rising chirps.
        for (const k of [0, 0.12]) {
          const g = shaped(ctx, out, at + k, 0.02, 0.005, 0.02, 0.05);
          const o = tone(ctx, "sine", hz(n.midi), g, at + k, 0.08);
          o.frequency.exponentialRampToValueAtTime(hz(n.midi + 5), at + k + 0.06);
        }
        break;
      }
      case "drum": {
        // A war drum: a deep thud falling in pitch, with the skin's slap.
        const g = shaped(ctx, out, at, 0.16, 0.005, 0.02, 0.6);
        const o = tone(ctx, "sine", 75, g, at, 0.65);
        o.frequency.exponentialRampToValueAtTime(40, at + 0.3);
        const slap = ctx.createBufferSource();
        slap.buffer = noise;
        slap.connect(lowpass(ctx, 250, shaped(ctx, out, at, 0.08, 0.002, 0, 0.08)));
        slap.start(at);
        slap.stop(at + 0.1);
        break;
      }
      case "gallop": {
        // A hoof: a short thud, the last of the three the heaviest.
        const g = shaped(ctx, out, at, n.midi ? 0.05 : 0.09, 0.003, 0.01, 0.15);
        const o = tone(ctx, "sine", 120, g, at, 0.2);
        o.frequency.exponentialRampToValueAtTime(60, at + 0.12);
        break;
      }
      case "handdrum": {
        if (n.midi === 0) {
          // Doum: the drum's deep middle.
          const o = tone(ctx, "sine", 110, shaped(ctx, out, at, 0.1, 0.003, 0.01, 0.3), at, 0.35);
          o.frequency.exponentialRampToValueAtTime(70, at + 0.2);
        } else {
          // Tek: a slap at its rim.
          hiss(ctx, noise, bandpass(ctx, 2500, 1.5, shaped(ctx, out, at, 0.06, 0.002, 0, 0.06)), at, 0.08);
          tone(ctx, "sine", 420, shaped(ctx, out, at, 0.025, 0.002, 0, 0.05), at, 0.06);
        }
        break;
      }
      case "hawk": {
        // A long cry falling away, high and far off.
        const g = shaped(ctx, out, at, 0.012, 0.04, 0.15, 0.4);
        const o = tone(ctx, "sine", hz(n.midi), g, at, 0.6);
        o.frequency.exponentialRampToValueAtTime(hz(n.midi - 7), at + 0.55);
        break;
      }
      case "wind":
      case "waves": {
        // Wind through a band that rises and falls; the sea is the same,
        // lower and fuller.
        const sea = n.voice === "waves";
        const src = ctx.createBufferSource();
        src.buffer = noise;
        src.loop = true;
        const band = ctx.createBiquadFilter();
        band.type = sea ? "lowpass" : "bandpass";
        band.Q.value = 0.8;
        band.frequency.setValueAtTime(sea ? 250 : 400, at);
        band.frequency.linearRampToValueAtTime(900, at + dur / 2);
        band.frequency.linearRampToValueAtTime(sea ? 250 : 350, at + dur);
        band.connect(shaped(ctx, out, at, sea ? 0.06 : 0.05, dur / 2, 0, dur / 2));
        src.connect(band);
        src.start(at);
        src.stop(at + dur + 0.05);
        break;
      }
      case "drip": {
        // A drop, and the cave giving it back a little later and quieter.
        for (const [k, peak] of [[0, 0.05], [0.23, 0.015]]) {
          const g = shaped(ctx, out, at + k, peak, 0.002, 0, 0.12);
          const o = tone(ctx, "sine", hz(n.midi), g, at + k, 0.13);
          o.frequency.exponentialRampToValueAtTime(hz(n.midi - 12), at + k + 0.05);
        }
        break;
      }
      case "creak": {
        // A bough creaking: a low buzz sagging in pitch.
        const o = tone(ctx, "sawtooth", hz(n.midi), shaped(ctx, lowpass(ctx, 400, out), at, 0.03, 0.05, 0.2, 0.3), at, 0.6);
        o.frequency.linearRampToValueAtTime(hz(n.midi) * 0.8, at + 0.55);
        break;
      }
      case "rustle":
        // Leaves stirring: a breath of high noise.
        hiss(ctx, noise, bandpass(ctx, 3000, 0.7, shaped(ctx, out, at, 0.025, 0.05, 0.1, 0.25)), at, 0.45);
        break;
      case "chime":
        // A glint, high and glassy, ringing on.
        tone(ctx, "sine", hz(n.midi), shaped(ctx, out, at, 0.015, 0.005, 0, 1.2), at, 1.2);
        tone(ctx, "sine", hz(n.midi) * 2.4, shaped(ctx, out, at, 0.004, 0.005, 0, 0.4), at, 0.4);
        break;
      case "bell":
        // A great bell a long way off.
        tone(ctx, "sine", hz(n.midi), shaped(ctx, lowpass(ctx, 1200, out), at, 0.03, 0.005, 0, beat * 3), at, beat * 3);
        tone(ctx, "sine", hz(n.midi) * 2.76, shaped(ctx, out, at, 0.006, 0.005, 0, beat), at, beat);
        break;
      case "anvil":
        // A hammer on an anvil, twice, ringing.
        for (const [k, peak] of [[0, 0.03], [0.18, 0.02]]) {
          tone(ctx, "sine", hz(n.midi), shaped(ctx, out, at + k, peak, 0.002, 0, 0.5), at + k, 0.5);
          tone(ctx, "sine", hz(n.midi) * 2.76, shaped(ctx, out, at + k, peak / 3, 0.002, 0, 0.2), at + k, 0.2);
        }
        break;
      case "whisper": {
        // Whispering: narrow noise, fluttering.
        const g = shaped(ctx, out, at, 0.02, 0.3, dur * 0.3, 0.4);
        const flutter = ctx.createGain();
        flutter.gain.value = 0.5;
        const lfo = ctx.createOscillator();
        lfo.frequency.value = 11;
        const depth = ctx.createGain();
        depth.gain.value = 0.5;
        lfo.connect(depth);
        depth.connect(flutter.gain);
        flutter.connect(g);
        lfo.start(at);
        lfo.stop(at + dur + 0.8);
        hiss(ctx, noise, bandpass(ctx, 2500, 3, flutter), at, dur + 0.7);
        break;
      }
    }
  }
}

/** How long one piece takes to fade into the next, in seconds. */
export const FADE_S = 4;
/** How far ahead of the ear the bars are written, in seconds. */
const AHEAD_S = 2.5;

/** One piece playing: its own gain, so it can be faded without the others. */
class Playing {
  readonly gain: GainNode;
  private bar = 0;
  private next: number;
  private tune: Tune;
  private timer: ReturnType<typeof setInterval>;

  constructor(
    private ctx: AudioContext,
    out: AudioNode,
    readonly track: Track,
    private noise: AudioBuffer,
  ) {
    const p = PIECES[track];
    this.tune = { step: Math.floor((p.low + p.high) / 2) };
    this.gain = ctx.createGain();
    this.gain.gain.setValueAtTime(0.0001, ctx.currentTime);
    this.gain.gain.exponentialRampToValueAtTime(1, ctx.currentTime + FADE_S);
    this.gain.connect(out);
    this.next = ctx.currentTime + 0.1;
    this.write();
    this.timer = setInterval(() => this.write(), 500);
  }

  private write() {
    const ctx = this.ctx;
    // A tab that was asleep does not play the bars it missed all at once.
    if (this.next < ctx.currentTime) this.next = ctx.currentTime + 0.05;
    const bar = (BEATS * 60) / PIECES[this.track].bpm;
    while (this.next < ctx.currentTime + AHEAD_S) {
      renderBar(ctx, this.gain, this.track, barNotes(this.track, this.bar++, this.tune), this.next, this.noise);
      this.next += bar;
    }
  }

  /** Fades out, and lets go of everything once it is silent. */
  end() {
    const now = this.ctx.currentTime;
    this.gain.gain.cancelScheduledValues(now);
    this.gain.gain.setValueAtTime(Math.max(0.0001, this.gain.gain.value), now);
    this.gain.gain.exponentialRampToValueAtTime(0.0001, now + FADE_S);
    clearInterval(this.timer);
    setTimeout(() => this.gain.disconnect(), (FADE_S + AHEAD_S + 1) * 1000);
  }
}

/** How loud the music is against the sound effects. */
export const MUSIC_GAIN = 0.35;

/** The music: whichever piece is wanted, faded across when that changes. */
export class Music {
  private out: GainNode;
  private playing: Playing | null = null;

  constructor(
    private ctx: AudioContext,
    master: AudioNode,
    private noise: AudioBuffer,
  ) {
    this.out = ctx.createGain();
    this.out.gain.value = MUSIC_GAIN;
    this.out.connect(master);
  }

  /** The piece playing, or null for none. */
  get track(): Track | null {
    return this.playing?.track ?? null;
  }

  /** Plays `track`, or nothing for null; the same track again changes nothing. */
  play(track: Track | null) {
    if ((this.playing?.track ?? null) === track) return;
    this.playing?.end();
    this.playing = track ? new Playing(this.ctx, this.out, track, this.noise) : null;
  }
}
